<template>
    <FormInput :context="context">
        <PrimeVueInputDropdown
            v-model="value"
            v-bind="attributes"
            v-on="$listeners"
            v-if="!isMultiple && !isAutocomplete"
        />
        <PrimeVueInputMultiSelect
            v-model="value"
            v-bind="attributes"
            v-on="$listeners"
            v-if="isMultiple && !isAutocomplete"
        />
        <PrimeVueInputAutoComplete
            v-model="value"
            v-bind="attributes"
            v-on="$listeners"
            @complete="onComplete"
            v-if="!isMultiple && isAutocomplete"
        />
    </FormInput>
</template>

<script>
import FormInputMixin from "@/components/forms/inputs/mixins/FormInputMixin";
import PrimeVueInputDropdown from "primevue/dropdown";
import PrimeVueInputMultiSelect from "primevue/multiselect";
import PrimeVueInputAutoComplete from "primevue/autocomplete";
import { suppliosApi } from "@/js/api-service";
import { get } from "lodash";

export default {
    components: {
        PrimeVueInputDropdown,
        PrimeVueInputMultiSelect,
        PrimeVueInputAutoComplete,
    },
    mixins: [FormInputMixin],
    props: {
        multiple: {
            type: Boolean,
            default: false,
        },
        autocomplete: {
            type: Boolean,
            default: false,
        },
        forceSelection: {
            type: Boolean,
            default: false,
        },
        requestConfig: {
            type: Array,
            default: () => [],
        },
        url: {
            type: String,
        },
        apiMethod: {
            type: String,
        },
        optionsKey: {
            type: String,
            default: "data",
        },
        optionLabel: {
            type: String,
            default: "name",
        },
        optionValue: {
            type: String,
            default: "id",
        },
        excludeSuggestion: {
            type: [String, Object],
        },
    },
    data() {
        return {
            query: null,
            optionsRemote: null,
            // may have to add 'emptyValue' here, or as a prop - in case backend does not allow null, only "" or [] - ideally should be part of schema, then passed in as prop
        };
    },
    watch: {
        url: {
            immediate: true,
            handler() {
                if (
                    (this.url && this.url !== "") ||
                    (this.apiMethod && this.apiMethod !== "")
                ) {
                    this.fetchOptions();
                }
            },
        },
    },
    computed: {
        defaults() {
            let defaults = {};

            if (this.isMultiple) {
                Object.assign(defaults, {
                    display: "chip",
                    showToggleAll: true,
                });
            } else {
                Object.assign(defaults, {
                    showClear: true,
                });
            }

            if (this.isAutocomplete) {
                Object.assign(defaults, {
                    dropdown: false,
                    autoHighlight: true,
                    suggestions: this.suggestions,
                    multiple: this.multiple,
                    field: this.optionLabel,
                    forceSelection: this.forceSelection,
                });
            } else {
                Object.assign(defaults, {
                    filter: true,
                    options: this.options,
                    optionLabel: this.optionLabel,
                    optionValue: this.optionValue,
                    dataKey: this.optionValue,
                });
            }

            return defaults;
        },
        options() {
            return (this.optionsRemote || this.context.options || [])
                .map((option) => {
                    // options was passed as a simple array of values and transformed by formulate
                    if (
                        option["id"] &&
                        String(option["id"]).includes("formulate-global-")
                    ) {
                        option[this.optionLabel] = option["label"];
                        option[this.optionValue] = option["value"];
                    }

                    return option;
                })
                .filter((option) => {
                    if (this.query && this.query !== "") {
                        if (
                            option[this.optionLabel]
                                ?.toLowerCase()
                                ?.startsWith(this.query.toLowerCase()) ||
                            option[this.optionValue]
                                ?.toLowerCase()
                                ?.startsWith(this.query.toLowerCase())
                        ) {
                            return true;
                        } else {
                            return false;
                        }
                    }

                    return true;
                })
                .filter((value, index, self) => {
                    return self.indexOf(value) === index;
                });
        },
        suggestions() {
            return this.options.filter((option) => {
                if (this.excludeSuggestion && this.excludeSuggestion !== "") {
                    return !JSON.stringify(option).includes(
                        this.excludeSuggestion
                    );
                }

                return option;
            });
        },
        isMultiple() {
            return (
                this.context.type === "select_multiple" ||
                (this.multiple && !this.isAutocomplete)
            );
        },
        isAutocomplete() {
            return this.context.type === "autocomplete" || this.autocomplete;
        },
    },
    methods: {
        async fetchOptions() {
            this.isLoading = true;

            let response;

            if (this.url && this.url !== "") {
                response = await this.fetchFromURL();
            }

            if (this.apiMethod && this.apiMethod !== "") {
                response = await this.fetchFromAPI();
            }

            if (response) {
                let data = response;
                if (this.optionsKey) {
                    data = get(response, this.optionsKey);
                }
                this.optionsRemote = data;
            }

            this.isLoading = false;
        },
        async fetchFromAPI() {
            const methods = this.apiMethod.split(".");

            let response;

            if (this.query && this.query !== "") {
                response = await suppliosApi[methods[0]][methods[1]](
                    this.query,
                    ...this.requestConfig
                );
            } else {
                response = await suppliosApi[methods[0]][methods[1]](
                    ...this.requestConfig
                );
            }

            return response.data;
        },
        async fetchFromURL() {
            let response;

            if (this.query && this.query !== "") {
                // TODO: add query params
                response = await fetch(this.url, ...this.requestConfig);
            } else {
                response = await fetch(this.url, ...this.requestConfig);
            }

            return response.json();
        },
        onValueChanged(value) {
            console.log(
                "hello from value watcher in SELECT COMPONENT for label:",
                this.context.label
            );
            this.context.model = value?.[this.optionValue] || value; // optional chaining added by MH to fix console warning... not sure of other implications....
        },
        parseValue(value) {
            console.log(
                "from parseValue in FormInputSelect - value=",
                value,
                "type=",
                typeof value
            );
            if (this.multiple && !Array.isArray(value)) {
                return value
                    .split(/[,|]/)
                    .filter((value) => value && value !== "");
            }

            if (value && value !== "") {
                return value;
            }

            return null;
        },
        onComplete($event) {
            this.query = $event.query.trim();
            this.fetchOptions();
        },
    },
};
</script>

<style lang="sass">
.p-autocomplete .p-autocomplete-multiple-container .p-autocomplete-input-token input
    border: 0
</style>
